import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import ImageUploader from '../components/ImageUploader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Camera, CreditCard, Building2, Shield, Check, Upload, 
  Loader2, Gift, Percent, AlertCircle, User, FileText
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfileSetup() {
  const { user, getAuthHeaders, refreshUser } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [profileStatus, setProfileStatus] = useState(null);
  const [profilePicture, setProfilePicture] = useState(user?.picture || null);
  const [uploading, setUploading] = useState(false);
  
  // ID Verification
  const [idImages, setIdImages] = useState([]);
  const [selfieImage, setSelfieImage] = useState([]);
  const [idType, setIdType] = useState('drivers_license');
  const [submittingId, setSubmittingId] = useState(false);
  
  // Payment Method
  const [paymentType, setPaymentType] = useState('card');
  const [savingPayment, setSavingPayment] = useState(false);
  const [cardData, setCardData] = useState({
    number: '',
    expiry: '',
    cvv: '',
    name: ''
  });
  const [bankData, setBankData] = useState({
    bankName: '',
    accountHolderName: '',
    routingNumber: '',
    accountNumber: '',
    isAutopay: true
  });
  
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchProfileStatus();
  }, []);

  const fetchProfileStatus = async () => {
    try {
      const response = await axios.get(
        `${API}/profile/status`,
        { headers: getAuthHeaders(), withCredentials: true }
      );
      setProfileStatus(response.data);
      setProfilePicture(user?.picture || null);
    } catch (error) {
      toast.error('Failed to load profile status');
    } finally {
      setLoading(false);
    }
  };

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const uploadResponse = await axios.post(
        `${API}/upload`,
        formData,
        { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }, withCredentials: true }
      );
      
      const pictureUrl = uploadResponse.data.url;
      
      await axios.put(
        `${API}/profile/picture`,
        { picture_url: pictureUrl },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      setProfilePicture(pictureUrl);
      setProfileStatus(prev => ({ ...prev, profile_complete: true, has_picture: true }));
      toast.success('Profile picture updated!');
      refreshUser?.();
    } catch (error) {
      toast.error('Failed to upload profile picture');
    } finally {
      setUploading(false);
    }
  };

  const handleIdVerification = async () => {
    if (idImages.length === 0) {
      toast.error('Please upload at least the front of your ID');
      return;
    }
    
    setSubmittingId(true);
    try {
      await axios.post(
        `${API}/profile/id-verification`,
        {
          id_type: idType,
          id_front_url: idImages[0],
          id_back_url: idImages[1] || null,
          selfie_url: selfieImage[0] || null
        },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      setProfileStatus(prev => ({ ...prev, id_verified: true, id_verification_status: 'approved' }));
      toast.success('ID verification approved!');
    } catch (error) {
      toast.error('Failed to submit ID verification');
    } finally {
      setSubmittingId(false);
    }
  };

  const handleAddCard = async () => {
    if (!cardData.number || !cardData.expiry || !cardData.name) {
      toast.error('Please fill in all card details');
      return;
    }
    
    setSavingPayment(true);
    try {
      const cardType = getCardType(cardData.number);
      
      await axios.post(
        `${API}/payment-methods`,
        {
          method_type: 'card',
          card_last_four: cardData.number.slice(-4),
          card_type: cardType,
          card_expiry: cardData.expiry
        },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      setProfileStatus(prev => ({ ...prev, has_payment_method: true }));
      setCardData({ number: '', expiry: '', cvv: '', name: '' });
      toast.success('Card added successfully!');
      fetchProfileStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add card');
    } finally {
      setSavingPayment(false);
    }
  };

  const handleAddBank = async () => {
    if (!profileStatus?.id_verified) {
      toast.error('Please complete ID verification first');
      return;
    }
    
    if (!bankData.bankName || !bankData.accountHolderName || !bankData.routingNumber || !bankData.accountNumber) {
      toast.error('Please fill in all bank details');
      return;
    }
    
    setSavingPayment(true);
    try {
      const response = await axios.post(
        `${API}/payment-methods`,
        {
          method_type: 'bank',
          bank_name: bankData.bankName,
          account_holder_name: bankData.accountHolderName,
          routing_number: bankData.routingNumber,
          account_last_four: bankData.accountNumber.slice(-4),
          is_autopay: bankData.isAutopay
        },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      setProfileStatus(prev => ({ 
        ...prev, 
        has_payment_method: true,
        has_bank_autopay: bankData.isAutopay,
        service_discount: bankData.isAutopay ? 10 : 0
      }));
      setBankData({ bankName: '', accountHolderName: '', routingNumber: '', accountNumber: '', isAutopay: true });
      toast.success(response.data.message || 'Bank account added!');
      fetchProfileStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add bank account');
    } finally {
      setSavingPayment(false);
    }
  };

  const getCardType = (number) => {
    const cleaned = number.replace(/\D/g, '');
    if (/^4/.test(cleaned)) return 'visa';
    if (/^5[1-5]/.test(cleaned)) return 'mastercard';
    if (/^3[47]/.test(cleaned)) return 'amex';
    if (/^6/.test(cleaned)) return 'discover';
    return 'card';
  };

  const formatCardNumber = (value) => {
    return value.replace(/\D/g, '').replace(/(\d{4})/g, '$1 ').trim().slice(0, 19);
  };

  const formatExpiry = (value) => {
    return value.replace(/\D/g, '').replace(/(\d{2})(\d)/, '$1/$2').slice(0, 5);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="profile-setup">
      <Header />
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Account Setup</h1>
          <p className="text-gray-500 mt-1">Complete your profile to unlock all features</p>
        </div>

        {/* Benefits Banner */}
        {profileStatus?.fee_waiver_active && (
          <Card className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <Gift className="w-8 h-8 text-green-600" />
                <div>
                  <p className="font-semibold text-green-800">2026 Welcome Bonus Active!</p>
                  <p className="text-sm text-green-600">
                    Your 1% marketplace fee is waived until {new Date(profileStatus.fee_waiver_until).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {profileStatus?.has_bank_autopay && (
          <Card className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <Percent className="w-8 h-8 text-blue-600" />
                <div>
                  <p className="font-semibold text-blue-800">10% Service Discount Active!</p>
                  <p className="text-sm text-blue-600">
                    You save 10% on all roadside assistance and mobile mechanic services with auto-pay
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Progress Steps */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <div className={`p-4 rounded-lg border-2 ${profileStatus?.has_picture ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
            <div className="flex items-center gap-2 mb-1">
              {profileStatus?.has_picture ? (
                <Check className="w-5 h-5 text-green-600" />
              ) : (
                <Camera className="w-5 h-5 text-gray-400" />
              )}
              <span className="font-medium text-sm">Profile Photo</span>
            </div>
            <p className="text-xs text-gray-500">{profileStatus?.has_picture ? 'Complete' : 'Required'}</p>
          </div>
          
          <div className={`p-4 rounded-lg border-2 ${profileStatus?.id_verified ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
            <div className="flex items-center gap-2 mb-1">
              {profileStatus?.id_verified ? (
                <Check className="w-5 h-5 text-green-600" />
              ) : (
                <FileText className="w-5 h-5 text-gray-400" />
              )}
              <span className="font-medium text-sm">ID Verified</span>
            </div>
            <p className="text-xs text-gray-500">{profileStatus?.id_verified ? 'Approved' : 'For bank payments'}</p>
          </div>
          
          <div className={`p-4 rounded-lg border-2 ${profileStatus?.has_payment_method ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
            <div className="flex items-center gap-2 mb-1">
              {profileStatus?.has_payment_method ? (
                <Check className="w-5 h-5 text-green-600" />
              ) : (
                <CreditCard className="w-5 h-5 text-gray-400" />
              )}
              <span className="font-medium text-sm">Payment Method</span>
            </div>
            <p className="text-xs text-gray-500">{profileStatus?.has_payment_method ? 'Added' : 'Required'}</p>
          </div>
          
          <div className={`p-4 rounded-lg border-2 ${profileStatus?.has_bank_autopay ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
            <div className="flex items-center gap-2 mb-1">
              {profileStatus?.has_bank_autopay ? (
                <Check className="w-5 h-5 text-green-600" />
              ) : (
                <Percent className="w-5 h-5 text-gray-400" />
              )}
              <span className="font-medium text-sm">10% Discount</span>
            </div>
            <p className="text-xs text-gray-500">{profileStatus?.has_bank_autopay ? 'Active' : 'Bank auto-pay'}</p>
          </div>
        </div>

        <Tabs defaultValue="photo" className="space-y-6">
          <TabsList className="bg-white border w-full justify-start">
            <TabsTrigger value="photo" className="flex items-center gap-2">
              <Camera className="w-4 h-4" /> Profile Photo
            </TabsTrigger>
            <TabsTrigger value="id" className="flex items-center gap-2">
              <Shield className="w-4 h-4" /> ID Verification
            </TabsTrigger>
            <TabsTrigger value="payment" className="flex items-center gap-2">
              <CreditCard className="w-4 h-4" /> Payment Methods
            </TabsTrigger>
          </TabsList>

          {/* Profile Photo Tab */}
          <TabsContent value="photo">
            <Card>
              <CardHeader>
                <CardTitle>Profile Photo</CardTitle>
                <CardDescription>Add a profile photo so others can recognize you</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-6">
                  <div className="w-32 h-32 rounded-full bg-gray-100 border-4 border-gray-200 overflow-hidden flex items-center justify-center">
                    {profilePicture ? (
                      <img src={profilePicture} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-16 h-16 text-gray-300" />
                    )}
                  </div>
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      capture="user"
                      onChange={handleProfilePictureUpload}
                      className="hidden"
                    />
                    <Button 
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="mb-2"
                    >
                      {uploading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4 mr-2" />
                      )}
                      {profilePicture ? 'Change Photo' : 'Upload Photo'}
                    </Button>
                    <p className="text-sm text-gray-500">
                      Use your device camera or upload from gallery
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ID Verification Tab */}
          <TabsContent value="id">
            <Card>
              <CardHeader>
                <CardTitle>ID Verification</CardTitle>
                <CardDescription>
                  Required for bank account payments. Your ID is securely processed.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {profileStatus?.id_verified ? (
                  <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
                    <Check className="w-8 h-8 text-green-600" />
                    <div>
                      <p className="font-semibold text-green-800">ID Verified</p>
                      <p className="text-sm text-green-600">You can now add bank accounts</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div>
                      <Label>ID Type</Label>
                      <Select value={idType} onValueChange={setIdType}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="drivers_license">Driver's License</SelectItem>
                          <SelectItem value="state_id">State ID</SelectItem>
                          <SelectItem value="passport">Passport</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>ID Photo (Front & Back)</Label>
                      <p className="text-sm text-gray-500 mb-2">Take a clear photo of your ID</p>
                      <ImageUploader
                        images={idImages}
                        setImages={setIdImages}
                        maxImages={2}
                        getAuthHeaders={getAuthHeaders}
                      />
                    </div>

                    <div>
                      <Label>Selfie (Optional)</Label>
                      <p className="text-sm text-gray-500 mb-2">Take a selfie holding your ID</p>
                      <ImageUploader
                        images={selfieImage}
                        setImages={setSelfieImage}
                        maxImages={1}
                        getAuthHeaders={getAuthHeaders}
                      />
                    </div>

                    <Button 
                      onClick={handleIdVerification}
                      disabled={submittingId || idImages.length === 0}
                      className="w-full"
                    >
                      {submittingId ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Shield className="w-4 h-4 mr-2" />
                      )}
                      Submit for Verification
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payment Methods Tab */}
          <TabsContent value="payment">
            <Card>
              <CardHeader>
                <CardTitle>Payment Methods</CardTitle>
                <CardDescription>
                  Add a card or bank account for payments. Bank auto-pay gets 10% off all services!
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Existing Payment Methods */}
                {profileStatus?.payment_methods?.length > 0 && (
                  <div className="mb-6 space-y-3">
                    <Label>Saved Payment Methods</Label>
                    {profileStatus.payment_methods.map((pm) => (
                      <div key={pm.payment_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          {pm.method_type === 'card' ? (
                            <CreditCard className="w-5 h-5 text-gray-600" />
                          ) : (
                            <Building2 className="w-5 h-5 text-gray-600" />
                          )}
                          <div>
                            <p className="font-medium">{pm.display_name}</p>
                            {pm.is_autopay && (
                              <span className="text-xs text-green-600 flex items-center gap-1">
                                <Check className="w-3 h-3" /> Auto-pay enabled • 10% discount
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add New Payment Method */}
                <Tabs value={paymentType} onValueChange={setPaymentType}>
                  <TabsList className="mb-4">
                    <TabsTrigger value="card" className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4" /> Card
                    </TabsTrigger>
                    <TabsTrigger value="bank" className="flex items-center gap-2">
                      <Building2 className="w-4 h-4" /> Bank Account
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="card" className="space-y-4">
                    <div>
                      <Label htmlFor="cardName">Name on Card</Label>
                      <Input
                        id="cardName"
                        value={cardData.name}
                        onChange={(e) => setCardData(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <Label htmlFor="cardNumber">Card Number</Label>
                      <Input
                        id="cardNumber"
                        value={cardData.number}
                        onChange={(e) => setCardData(prev => ({ ...prev, number: formatCardNumber(e.target.value) }))}
                        placeholder="1234 5678 9012 3456"
                        maxLength={19}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="expiry">Expiry</Label>
                        <Input
                          id="expiry"
                          value={cardData.expiry}
                          onChange={(e) => setCardData(prev => ({ ...prev, expiry: formatExpiry(e.target.value) }))}
                          placeholder="MM/YY"
                          maxLength={5}
                        />
                      </div>
                      <div>
                        <Label htmlFor="cvv">CVV</Label>
                        <Input
                          id="cvv"
                          type="password"
                          value={cardData.cvv}
                          onChange={(e) => setCardData(prev => ({ ...prev, cvv: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                          placeholder="123"
                          maxLength={4}
                        />
                      </div>
                    </div>
                    <Button onClick={handleAddCard} disabled={savingPayment} className="w-full">
                      {savingPayment ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Add Card
                    </Button>
                  </TabsContent>

                  <TabsContent value="bank" className="space-y-4">
                    {!profileStatus?.id_verified && (
                      <div className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg mb-4">
                        <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-yellow-800">ID Verification Required</p>
                          <p className="text-sm text-yellow-700">
                            Please complete ID verification before adding a bank account.
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-2 text-green-700 font-medium mb-1">
                        <Percent className="w-5 h-5" />
                        Save 10% with Bank Auto-Pay!
                      </div>
                      <p className="text-sm text-green-600">
                        Set up automatic bank payments and get 10% off all roadside assistance and mobile mechanic services.
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="bankName">Bank Name</Label>
                      <Input
                        id="bankName"
                        value={bankData.bankName}
                        onChange={(e) => setBankData(prev => ({ ...prev, bankName: e.target.value }))}
                        placeholder="Chase Bank"
                        disabled={!profileStatus?.id_verified}
                      />
                    </div>
                    <div>
                      <Label htmlFor="accountHolder">Account Holder Name</Label>
                      <Input
                        id="accountHolder"
                        value={bankData.accountHolderName}
                        onChange={(e) => setBankData(prev => ({ ...prev, accountHolderName: e.target.value }))}
                        placeholder="John Doe"
                        disabled={!profileStatus?.id_verified}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="routing">Routing Number</Label>
                        <Input
                          id="routing"
                          value={bankData.routingNumber}
                          onChange={(e) => setBankData(prev => ({ ...prev, routingNumber: e.target.value.replace(/\D/g, '').slice(0, 9) }))}
                          placeholder="123456789"
                          maxLength={9}
                          disabled={!profileStatus?.id_verified}
                        />
                      </div>
                      <div>
                        <Label htmlFor="account">Account Number</Label>
                        <Input
                          id="account"
                          value={bankData.accountNumber}
                          onChange={(e) => setBankData(prev => ({ ...prev, accountNumber: e.target.value.replace(/\D/g, '') }))}
                          placeholder="1234567890"
                          disabled={!profileStatus?.id_verified}
                        />
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="autopay"
                        checked={bankData.isAutopay}
                        onCheckedChange={(checked) => setBankData(prev => ({ ...prev, isAutopay: checked }))}
                        disabled={!profileStatus?.id_verified}
                      />
                      <label htmlFor="autopay" className="text-sm font-medium">
                        Enable automatic payments (Get 10% off all services!)
                      </label>
                    </div>
                    <Button 
                      onClick={handleAddBank} 
                      disabled={savingPayment || !profileStatus?.id_verified} 
                      className="w-full"
                    >
                      {savingPayment ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Add Bank Account
                    </Button>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Continue Button */}
        <div className="mt-8 flex justify-end">
          <Button 
            onClick={() => navigate('/customer')}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Continue to Dashboard
          </Button>
        </div>
      </main>
    </div>
  );
}
